/**
 * LVT FullStory integration lib.
 *
 * The FullStory library is implemented in combination with React's Contexts.
 *
 * Note: You can import this in two different ways, depending on your needs:
 *  - If you just need the class/event names and not any functions of the api:
 *      import * as FullStory from "@/utilities/hooks/fullstory"
 *
 *  - If you need FullStory api functions:
 *      import { useFullStory } from "@/utilities/hooks/fullstory";
 *      // In Component
 *      const FullStory = useFullStory();
 *
 *  It's recommended to use the above two as they produce the same API when used
 *  (i.g. Fullstory.names) allowing consistency across components that do and do not
 *  use api functionality, while also allowing non-api users to skip using the hook.
 *
 * */
import * as FullStory from "@fullstory/browser";
import { UserVars } from "@fullstory/browser";
import React, { useContext } from "react";

/**
 * Constants relative to full story names. These are constants that are used as class names on elements, or custom
 * events sent via FullStory.event(). These will show up in FullStory for use in metrics.
 */
export const names = {
    classes: {
        searchResults: {
            viewStream: "data_button_search_viewstream",
            viewHistory: "data_button_search_viewHistory",
            viewUnit: "data_button_search_viewUnit",
        },
        searchField: {
            common: "data_field_search",
            map: "data_location_map",
            stream: "data_location_cameras",
            history: "data_location_history",
        },
        backButton: {
            common: "data_button_backtomap",
            stream: "data_location_cameras",
            history: "data_location_history",
        },
        searchDrawer: {
            anyCamera: "data_button_map_viewstream data_secondary_camerathumbnail",
            defaultCamera: "data_button_map_viewstream data_secondary_camerathumbnail data_tertiary_defaultcamera",
            streamButton: "data_button_map_viewstream data_secondary_map_drawer",
            historyButton: "data_button_map_viewhistory data_secondary_map_drawer",
        },
        streaming: {
            relayPrefix: "data_button_cameras_",
            suspendPatrol: "data_button_cameras_suspendpatrol",
            otherCamera: "data_button_cameras_cameraview data_secondary_camerathumbnail",
            snapshot: "data_button_cameras_snapshot",
            presetMenu: "data_menu_cameras_presets",
            preset: "data_select_cameras_preset",
            lightMenu: "data_menu_cameras_lights",
            light: "data_button_cameras_light",
            strobeLight: "data_secondary_strobelight",
            floodLight: "data_secondary_floodlight",
            cameraLight: "data_secondary_cameralight",
            soundMenu: "data_menu_cameras_sounds",
            quickSound: "data_button_cameras_quicksound",
        },
        history: {
            timelineDate: "data_field_history_timelinedate",
            viewAllSaved: "data_button_history_viewallsavedvideos",
            thumbnail: "data_button_history_largerview",
            selectVideo: "data_button_history_selectVideo",
            requestVideo: "data_button_history_requestVideo",
        },
        map: {
            filterMenu: "data_menu_map_filters",
            filterToggle: "data_toggle_map_filtertoggle",
        },
        navigation: {
            cameras: "data_button_unit_cameras",
            history: "data_button_unit_history",
        },
    },
    events: {
        mapMarkerClicked: "map_marker_clicked",
        mapTypeChanged: "map_type_changed",
        favoriteAdd: "data_toggle_cameras_favorite_add",
        favoriteRemove: "data_toggle_cameras_favorite_remove",
        historyDateChanged: "history_timelinedate_changed",
        streamPageLoaded: "stream_page_loaded",
        historyPageLoaded: "history_page_loaded",
    },
};

/**
* The interface of the Context used in our hook
**/
export interface ContextInterface {
    isEnabled: boolean;
    isInDevMode: boolean;
    isInitialized: boolean;
    isLoggingEnabled: boolean;
    orgId?: string;
}

/**
 * The config passed to setup, which is basically all the information in the ContextInterface
 * minus the isInitialized field that is determined via setup()
 */
export type ContextConfig = Omit<ContextInterface, "isInitialized">;

/**
 * Setup FullStory for usage, calling FullStory.init() in the process and
 * returning a ContextInterface so components can know the current state
 * of FullStory
 */
export const setup = (config: ContextConfig): ContextInterface => {
    let isInitialized = false;

    // orgId is required by FullStory to initialize.
    if (config.isEnabled && config.orgId) {
        const fullStoryConfig = { orgId: config.orgId, devMode: config.isInDevMode };

        try {
            FullStory.init(fullStoryConfig);
            isInitialized = true;
        } catch (e) {
            console.error("FullStory][setup] init(", fullStoryConfig, ") resulted in ", e);
        }
    }

    const results = { isInitialized, ...config };

    if (config.isLoggingEnabled) {
        console.log("[FullStory][setup]", results);
    }

    return results;
};

/**
 * The FullStory React Context. The object passed to this will be the state the context is in, *IF*
 * there is no Provider tag wrapping the component the context is used in.
 */
export const Context = React.createContext<ContextInterface>({ isEnabled: false, isInDevMode: false, isInitialized: false, isLoggingEnabled: false });

/**
 * FullStory Hook. (Must follow react hook rules)
 * This hook is the primary way to use FullStory. This hook wraps the FullStory module methods and uses the fullstory
 * Context to do any checks as to whether to make the api calls or not, as well as produce any logging.
 *
 * Note: If you need a function that's on the FullStory api, but not wrapped here, you can add them as needed.
 */
export const useFullStory = () => {
    const context = useContext(Context);

    const getModeDescription = () => context.isInDevMode ? "dev" : "live";

    const logPrefix = "[FullStory][" + getModeDescription() + "]";

    // Wrapper for FullStory.event().  Whether or not FullStory is properly enabled/initialized or not will be
    // handled
    const event = (eventName: string, eventProperties: { [key: string]: any }): void => {
        if (context.isEnabled && context.isInitialized) {
            try {
                FullStory.event(eventName, eventProperties);

                if (context.isLoggingEnabled) {
                    console.log(logPrefix, "event(", eventName, ",", eventProperties, ")");
                }
            } catch (e) {
                console.error(logPrefix, "event(", eventName, ",", eventProperties, ") resulted in ", e);
            }
        }
    };

    // Wrapper for FullStory.identify(). Whether or not FullStory is properly enabled/initialized or not will be
    // handled.
    const identify = (uid: string, customVars?: UserVars): void => {
        if (context.isEnabled && context.isInitialized) {
            try {
                FullStory.identify(uid, customVars);

                if (context.isLoggingEnabled) {
                    console.log(logPrefix, "identify(", uid, ",", customVars, ")");
                }
            } catch (e) {
                console.error(logPrefix, "identify(", uid, ",", customVars, ") resulted in ", e);
            }
        }
    };

    // Wrapper for FullStory.getCurrentSessionURL().  Whether or not FullStory is properly enabled/initialized or not will be
    // handled.
    const getCurrentSessionURL = (now?: boolean): string | null => {
        if (context.isEnabled && context.isInitialized) {
            try {
                const url = FullStory.getCurrentSessionURL(now);

                if (context.isLoggingEnabled) {
                    console.log(logPrefix, "getCurrentSessionURL(", now, ")");
                }

                return url;
            } catch (e) {
                console.error(logPrefix, "getCurrentSessionURL(", now, ") resulted in ", e);
            }
        }

        return null;
    };

    return { event, identify, getCurrentSessionURL, names };
};
